// API utilities extracted from App.jsx
export const fetchWaitlistStats = async () => {
  try {
    const response = await fetch("/api/stats");
    if (response.ok) {
      const data = await response.json();
      return { success: true, data: { totalUsers: data.totalUsers || 0 } };
    }
    throw new Error('Failed to fetch stats');
  } catch (error) {
    console.error("Failed to fetch stats:", error);
    // Fallback for demo/development
    return { success: false, error, fallbackData: { totalUsers: 0 } };
  }
};

export const signupToWaitlist = async (email, referredBy = null) => {
  try {
    // Safely get referral code from localStorage
    let fallbackReferral = null;
    try {
      if (typeof localStorage !== 'undefined') {
        fallbackReferral = localStorage.getItem("ffs_ref");
      }
    } catch (error) {
      console.warn('Error accessing localStorage for referral:', error);
    }

    const referralSent = referredBy || fallbackReferral || null;

    const response = await fetch("/api/signup", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        email: email.trim(),
        referredBy: referralSent,
      }),
    });

    const data = await response.json();

    if (response.ok || response.status === 409) {
      return {
        success: true,
        data: {
          referralCode: data.referralCode,
          referralLink: data.referralLink,
          position: data.position,
          referralCount: data.referralCount || 0,
          // Link code this signup came through (saved with the session)
          referredBy: referralSent,
        }
      };
    } else {
      return {
        success: false,
        error: data.error || "Something went wrong. Please try again."
      };
    }
  } catch (error) {
    // Network failure or a non-JSON error page: report it instead of faking a
    // signup, so nobody is shown a waitlist spot or referral link they don't have
    console.error("Signup error:", error);
    return {
      success: false,
      error: "Something went wrong. Please check your connection and try again."
    };
  }
};

// Short referral links: /r/CODE (served by the SPA fallback in public/_redirects)
const SHORT_REF_PATH = /^\/r\/([^/]+)\/?$/;

// Read a referral code from the current URL only (no localStorage fallback).
// Accepts the generated form `/?ref=CODE` and the short form `/r/CODE`.
// Codes are lowercase.
export const getUrlReferralCode = () => {
  try {
    if (typeof window === 'undefined') return null;

    const params = new URLSearchParams(window.location.search);
    const ref = params.get("ref");
    if (ref && ref.trim()) {
      return ref.trim().toLowerCase();
    }

    const match = window.location.pathname.match(SHORT_REF_PATH);
    return match ? decodeURIComponent(match[1]).trim().toLowerCase() : null;
  } catch (error) {
    console.warn('Error reading referral code from URL:', error);
    return null;
  }
};

// Remove the referral code from the address bar (?ref=CODE or /r/CODE) without
// reloading, so a copied URL doesn't pass on someone else's code. A pending
// referral is unaffected: it lives in state and localStorage.
export const clearReferralFromUrl = () => {
  try {
    if (typeof window === 'undefined' || !window.history?.replaceState) return;

    const url = new URL(window.location.href);
    const isShortLink = SHORT_REF_PATH.test(url.pathname);
    if (!url.searchParams.has('ref') && !isShortLink) return;

    url.searchParams.delete('ref');
    if (isShortLink) url.pathname = '/';
    window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
  } catch (error) {
    console.warn('Error clearing referral code from URL:', error);
  }
};

// Decide which view a visitor should see from their saved session (if any)
// and any referral code present in the URL (not the localStorage fallback):
// - no session                 -> signup form (existing behaviour)
// - session, no URL ref        -> joined/share view
// - session, own code          -> joined/share view
// - session, their invite code -> joined/share view (reopening the link they
//                                 signed up through)
// - session, any other code    -> signup form so another email can be credited
export const resolveLandingView = (savedSession, urlRef) => {
  const isOwnLink = !!(urlRef && savedSession && (
    urlRef === savedSession.referralCode || urlRef === savedSession.referredBy
  ));
  const isFriendRef = !!(urlRef && savedSession && !isOwnLink);

  return {
    hasJoined: !!savedSession && !!savedSession.hasJoined && !isFriendRef,
    isFriendRef,
  };
};

// Utility for handling referral codes
export const handleReferralCode = () => {
  try {
    if (typeof window === 'undefined') return null;

    const ref = getUrlReferralCode();

    if (ref) {
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem("ffs_ref", ref);
      }
      return ref;
    } else {
      if (typeof localStorage !== 'undefined') {
        return localStorage.getItem("ffs_ref");
      }
      return null;
    }
  } catch (error) {
    console.warn('Error handling referral code:', error);
    return null;
  }
};

export const clearReferralCode = () => {
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem("ffs_ref");
    }
  } catch (error) {
    console.warn('Error clearing referral code:', error);
  }
};